import type { BookChapter } from '@/components/books/book';
import { buildTocReorderPayload, isEmptyTocReorderPayload, reorderMaBookToc, type MaTocReorderPayload } from '@/lib/ma-books-api';

export type TocFlushDeps = {
    zybookCode: string;
    getOptimistic: () => BookChapter[];
    getLastConfirmed: () => BookChapter[];
    getDirty: () => boolean;
    setInFlight: (value: boolean) => void;
    setDirty: (value: boolean) => void;
    setOptimistic: (chapters: BookChapter[]) => void;
    setLastConfirmed: (chapters: BookChapter[]) => void;
    onError: (message: string) => void;
    onSuccess?: () => void;
};

export function createTocReorderFlush(deps: TocFlushDeps) {
    let inFlight = false;

    async function flushReorder(): Promise<void> {
        if (inFlight) {
            return;
        }
        if (!deps.getDirty()) {
            return;
        }

        inFlight = true;
        deps.setInFlight(true);
        const before = deps.getLastConfirmed();
        const after = deps.getOptimistic();
        deps.setDirty(false);

        let succeeded = false;
        try {
            const payload = buildTocReorderPayload(before, after);
            if (isEmptyTocReorderPayload(payload)) {
                deps.setLastConfirmed(after);
                succeeded = true;
            } else {
                await reorderMaBookToc(deps.zybookCode, payload);
                deps.setLastConfirmed(after);
                succeeded = true;
            }
        } catch (error) {
            deps.setOptimistic(deps.getLastConfirmed());
            deps.setDirty(false);
            deps.onError(error instanceof Error ? error.message : String(error));
        } finally {
            inFlight = false;
            deps.setInFlight(false);
            if (deps.getDirty()) {
                await flushReorder();
            } else if (succeeded) {
                deps.onSuccess?.();
            }
        }
    }

    return { flushReorder };
}

export type { MaTocReorderPayload };
